<template>
  <div>
    <div class="position-relative">
      <!-- shape Hero -->
      <section class="section-shaped my-0">
        <bubbles-background></bubbles-background>
        <div class="container container-lg shape-container pb-4">
          <card shadow class="card-profile mt-4 border-0" no-body>
            <vertical-ptwist-modules></vertical-ptwist-modules>

            <card-header title="Wordclouds" :icon="{prefix:'fas', iconName: 'comments'}" prefix="fas"
                         :text="pageText"></card-header>
            <div class="px-4 pb-3">
              <div class="row">
                <div class="col-md-4 col-sm-12">
                  <daterange-picker alternative></daterange-picker>
                </div>
                <div class="col-md-4 col-sm-12">
                  <collections-select v-model="collection" alternative
                                      label="Collection"></collections-select>
                </div>
                <div class="col-md-4 col-sm-12">
                  <language-select alternative label="Language"></language-select>
                </div>
              </div>
              <div class="mb-3 row z-index-1 position-relative">
                <div class="col-md-4">
                  <base-button tag="a"
                               class="mb-3 mb-sm-0"
                               type="button"
                               @click="changeWordcloudType('terms')"
                               :class="[ wordcloudType === 'terms' ? 'bg-blue text-white' : 'bg-secondary text-black-50']"
                               :icon="{ prefix: 'fas', iconName: 'language'}">
                    Terms
                  </base-button>
                  <base-button tag="a"
                               class="mb-3 mb-sm-0"
                               type="button"
                               @click="changeWordcloudType('hashtags')"
                               :class="[ wordcloudType === 'hashtags' ? 'bg-blue text-white' : 'bg-secondary text-black-50']"
                               :icon="{ prefix: 'fas', iconName: 'hashtag'}">
                    Hashtags
                  </base-button>
                </div>
              </div>
              <div v-if="words">
                <base-progress v-if="wordcloudProgress" type="primary"
                               :value="progress"
                               label="Generating Wordcloud"></base-progress>
                <vue-word-cloud
                  class="wordcloud"
                  v-on:update:progress="wordcloudProgress = $event"
                  :words="wordcloudType === 'terms' ? this.words.terms : this.words.hashtags"
                  :color="color"
                  font-family="Roboto">
                  >
                  <template slot-scope="{text, weight, word}">
                    <div class="wordcloud-word" :title="weight" style="cursor: pointer;"
                         @click="onWordClick(word)">
                      {{ text }}
                    </div>
                  </template>
                </vue-word-cloud>
              </div>
              <ptwist-loading v-else-if="loading"></ptwist-loading>
              <server-error v-else-if="error"></server-error>
              <no-data-error v-else></no-data-error>
            </div>
            <modal :show.sync="showModal">
              <template slot="header">
                <h5 class="modal-title" id="exampleModalLabel">Top tweets that contain the word
                  <strong>{{clickedWord }}</strong></h5>
              </template>
              <div>
                <div v-if="topTweetsByText">
                  <wc-top-tweets-masonry wordcloud v-if="topTweetsByText.length > 0"
                                         :tweets="topTweetsByText"></wc-top-tweets-masonry>
                  <no-data-error text="Clicked word cannot be found in top tweets"
                                 subtext="This word is included in some tweets, however it is not included in any
                                  of the tweets that have been categorized as one of the top ones (
                                  <a href='/prevalent-data/top-posts'> see top posts </a>
                                  )"
                                 v-else></no-data-error>
                </div>
              </div>
              <template slot="footer">
                <base-button type="primary" @click="showModal = false">ΟΚ</base-button>
              </template>
            </modal>
          </card>
        </div>
      </section>
    </div>
  </div>
</template>

<script>

import { Bus } from '../bus'
import { getRandomArrayIndex, convertRange } from '../utils'
import { getInfoText } from '../info'
import VerticalPtwistModules from '@/components/VerticalPtwistModules'

export default {
  name: 'wordclouds',
  components: { VerticalPtwistModules },
  data () {
    return {
      loading: true,
      collection: 'plastic_pollution',
      language: 'en',
      wordcloudType: 'terms',
      pageText: getInfoText('wordclouds'),
      wordcloudProgress: { completedWords: 0, totalWords: 200 },
      wordcloudReady: true,
      showModal: false,
      clickedWord: '',
      timestamps: null,
      topTweetsByText: null,
      words: null
    }
  },
  computed: {
    progress: function () {
      return parseInt(convertRange(this.wordcloudProgress.completedWords, [0, 200], [0, 100]))
    },
    color: function () {
      const colors = ['#179e81', '#ffc107', '#114B5F', '#88D498', '#C6DABF']
      return function () {
        return getRandomArrayIndex(colors)
      }
    }
  },
  methods: {
    onWordClick: async function (word) {
      this.clickedWord = word[0]

      this.topTweetsByText = []
      await this.$nextTick()

      let payload = {
        data: {
          collection: this.collection,
          language: this.language,
          timestamp_from: this.timestamps.from,
          timestamp_to: this.timestamps.to,
          text: this.clickedWord
        },
        endpoint: 'twitter/top-tweets/by-text'
      }
      this.$store
        .dispatch('api/get', payload)
        .then(response => {
          this.topTweetsByText = response.data
          this.showModal = true
        })
    },
    changeWordcloudType (type) {
      this.wordcloudType = type
    },
    updateWordcloud () {
      this.loading = true
      let payload = {
        data: {
          collection: this.collection,
          language: this.language,
          timestamp_from: this.timestamps.from,
          timestamp_to: this.timestamps.to
        },
        endpoint: 'twitter/wordclouds'
      }
      this.$store
        .dispatch('api/get', payload)
        .then(response => {
          this.words = this.scaleWords(response.data)
        }).catch(error => {
          console.error(error)
          this.error = true
        })
        .finally(() => {
          this.loading = false
        })
    },
    scaleWords (words) {
      const termsMax = words.terms[0][1]
      const termsMin = words.terms[words.terms.length - 1][1]
      const hashtagsMax = words.hashtags[0][1]
      const hashtagsMin = words.hashtags[words.hashtags.length - 1][1]
      words.terms = words.terms.map(term => {
        return [term[0], convertRange(term[1], [termsMin, termsMax], [2, 20])]
      })
      words.hashtags = words.hashtags.map(term => {
        return [term[0], convertRange(term[1], [hashtagsMin, hashtagsMax], [2, 20])]
      })
      return words
    }

  },
  watch: {
    timestamps () {
      this.updateWordcloud()
    },
    collection () {
      this.updateWordcloud()
    },
    language () {
      this.updateWordcloud()
    }
  },
  created () {
    Bus.$on('new-language', function (newLanguage) {
      this.language = newLanguage
      Bus.$emit('new-collections', this.language)
    }.bind(this))
    Bus.$on('new-collection', function (newCollection) {
      this.collection = newCollection
    }.bind(this))
    Bus.$on('daterange-update', function (timestamps) {
      this.timestamps = timestamps
    }.bind(this))
  }

}
</script>
<style lang="sass">
  .wordcloud
    width: 100%
    height: 500px !important

    .wordcloud-word:hover
      text-shadow: 1px 1px 0 rgb(101, 101, 101)

</style>
